import React, { useState, useMemo } from 'react'
import {
  Users,
  UserPlus,
  Search,
  ChevronLeft,
  ChevronRight,
  MoreVertical,
  X,
  Trash2,
  Power,
} from 'lucide-react'
import { UserStatus } from '../../types/enums'
import { USER_MANAGEMENT_CONFIG } from '../../constants/config'
import type { UserRole } from '../../types'

interface MockUser {
  id: string
  email: string
  role: UserRole
  institution: string
  university: string
  status: UserStatus
  last_login: string
}

const MOCK_USERS: MockUser[] = [
  { id: '1', email: 'admin@torrens.edu.au', role: 'system-admin', institution: 'Torrens University', university: 'Torrens University Australia', status: UserStatus.ACTIVE, last_login: '2026-02-04T08:30:00Z' },
  { id: '2', email: 'j.smith@torrens.edu.au', role: 'uni-admin', institution: 'Torrens University', university: 'Torrens University Australia', status: UserStatus.ACTIVE, last_login: '2026-02-03T14:15:00Z' },
  { id: '3', email: 'researcher1@torrens.edu.au', role: 'researcher', institution: 'Torrens University', university: 'Torrens University Australia', status: UserStatus.ACTIVE, last_login: '2026-02-02T10:00:00Z' },
  { id: '4', email: 'a.jones@anu.edu.au', role: 'researcher', institution: 'ANU', university: 'Australian National University', status: UserStatus.ACTIVE, last_login: '2026-02-01T09:45:00Z' },
  { id: '5', email: 'lib.manager@usyd.edu.au', role: 'institution-admin', institution: 'University of Sydney', university: 'University of Sydney', status: UserStatus.ACTIVE, last_login: '2026-01-30T16:20:00Z' },
  { id: '6', email: 'b.lee@unimelb.edu.au', role: 'researcher', institution: 'University of Melbourne', university: 'University of Melbourne', status: UserStatus.DISABLED, last_login: '2026-01-15T11:00:00Z' },
  { id: '7', email: 'c.watson@monash.edu', role: 'researcher', institution: 'Monash University', university: 'Monash University', status: UserStatus.ACTIVE, last_login: '2026-02-04T07:55:00Z' },
  { id: '8', email: 'dept.head@uq.edu.au', role: 'uni-admin', institution: 'University of Queensland', university: 'University of Queensland', status: UserStatus.ACTIVE, last_login: '2026-02-03T13:00:00Z' },
]

const ROLES: { value: UserRole; label: string }[] = [
  { value: 'researcher', label: 'Researcher' },
  { value: 'uni-admin', label: 'University Admin' },
  { value: 'institution-admin', label: 'Institution Admin' },
  { value: 'system-admin', label: 'System Admin' },
]

const PAGE_SIZE = USER_MANAGEMENT_CONFIG.PAGE_SIZE

function formatDate(ts: string): string {
  return new Date(ts).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function roleBadge(role: UserRole): React.ReactNode {
  const colors: Record<UserRole, string> = {
    'system-admin': 'bg-red-100 text-red-700',
    'institution-admin': 'bg-purple-100 text-purple-700',
    'uni-admin': 'bg-blue-100 text-blue-700',
    researcher: 'bg-green-100 text-green-700',
  }
  const labels: Record<UserRole, string> = {
    'system-admin': 'System Admin',
    'institution-admin': 'Institution Admin',
    'uni-admin': 'Uni Admin',
    researcher: 'Researcher',
  }
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors[role]}`}>
      {labels[role]}
    </span>
  )
}

export default function UserManagement() {
  const [users, setUsers] = useState<MockUser[]>(MOCK_USERS)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('')
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [page, setPage] = useState(1)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null)
  const [activeMenu, setActiveMenu] = useState<string | null>(null)

  // Invite form state
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<UserRole>('researcher')
  const [inviteInstitution, setInviteInstitution] = useState('')

  const institutions = useMemo(() => {
    return [...new Set(users.map((u) => u.institution))].sort()
  }, [users])

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (searchTerm && !u.email.toLowerCase().includes(searchTerm.toLowerCase())) return false
      if (roleFilter && u.role !== roleFilter) return false
      if (institutionFilter && u.institution !== institutionFilter) return false
      return true
    })
  }, [users, searchTerm, roleFilter, institutionFilter])

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE))
  const pagedUsers = filteredUsers.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  const handleToggleStatus = (userId: string) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId ? { ...u, status: u.status === UserStatus.ACTIVE ? UserStatus.DISABLED : UserStatus.ACTIVE } : u
      )
    )
    setActiveMenu(null)
  }

  const handleChangeRole = (userId: string, newRole: UserRole) => {
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u))
    )
    setActiveMenu(null)
  }

  const handleDelete = (userId: string) => {
    setUsers((prev) => prev.filter((u) => u.id !== userId))
    setShowDeleteConfirm(null)
    setActiveMenu(null)
  }

  const handleInvite = () => {
    if (!inviteEmail) return
    const newUser: MockUser = {
      id: String(Date.now()),
      email: inviteEmail,
      role: inviteRole,
      institution: inviteInstitution || 'Unassigned',
      university: inviteInstitution || 'Unassigned',
      status: UserStatus.ACTIVE,
      last_login: 'Never',
    }
    setUsers((prev) => [newUser, ...prev])
    setShowInviteModal(false)
    setInviteEmail('')
    setInviteRole('researcher')
    setInviteInstitution('')
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-6 h-6 text-acad-500" />
            User Management
          </h1>
          <p className="text-gray-500 mt-1">Manage user accounts and access roles</p>
        </div>
        <button
          onClick={() => setShowInviteModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-acad-500 text-white rounded-lg
                     text-sm font-medium hover:bg-acad-600 transition-colors"
        >
          <UserPlus className="w-4 h-4" />
          Invite User
        </button>
      </div>

      {/* Search / Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by email..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setPage(1) }}
              className="w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm
                         focus:outline-none focus:ring-2 focus:ring-acad-500"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => { setRoleFilter(e.target.value as UserRole | ''); setPage(1) }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white
                       focus:outline-none focus:ring-2 focus:ring-acad-500"
          >
            <option value="">All Roles</option>
            {ROLES.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <select
            value={institutionFilter}
            onChange={(e) => { setInstitutionFilter(e.target.value); setPage(1) }}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white
                       focus:outline-none focus:ring-2 focus:ring-acad-500"
          >
            <option value="">All Institutions</option>
            {institutions.map((inst) => (
              <option key={inst} value={inst}>{inst}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden lg:table-cell">Institution</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider hidden xl:table-cell">Last Login</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {pagedUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-sm text-gray-900 font-medium">{user.email}</td>
                  <td className="px-4 py-3">{roleBadge(user.role)}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 hidden lg:table-cell">{user.institution}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span
                      className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                        user.status === UserStatus.ACTIVE ? 'text-green-600' : 'text-gray-400'
                      }`}
                    >
                      <span
                        className={`w-1.5 h-1.5 rounded-full ${
                          user.status === UserStatus.ACTIVE ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      />
                      {user.status === UserStatus.ACTIVE ? 'Active' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-400 hidden xl:table-cell">
                    {user.last_login === 'Never' ? 'Never' : formatDate(user.last_login)}
                  </td>
                  <td className="px-4 py-3 text-right relative">
                    <button
                      onClick={() => setActiveMenu(activeMenu === user.id ? null : user.id)}
                      className="p-1 rounded hover:bg-gray-100 transition-colors"
                    >
                      <MoreVertical className="w-4 h-4 text-gray-400" />
                    </button>

                    {/* Dropdown Menu */}
                    {activeMenu === user.id && (
                      <div className="absolute right-4 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-10 py-1">
                        {/* Edit Role submenu */}
                        <div className="px-3 py-2">
                          <p className="text-xs font-medium text-gray-400 uppercase mb-1">Change Role</p>
                          <select
                            value={user.role}
                            onChange={(e) => handleChangeRole(user.id, e.target.value as UserRole)}
                            className="w-full border border-gray-200 rounded px-2 py-1 text-sm
                                       focus:outline-none focus:ring-1 focus:ring-acad-500"
                          >
                            {ROLES.map((r) => (
                              <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                          </select>
                        </div>
                        <div className="border-t border-gray-100 my-1" />
                        <button
                          onClick={() => handleToggleStatus(user.id)}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700
                                     hover:bg-gray-50 transition-colors"
                        >
                          <Power className="w-4 h-4" />
                          {user.status === UserStatus.ACTIVE ? 'Disable User' : 'Enable User'}
                        </button>
                        <button
                          onClick={() => { setShowDeleteConfirm(user.id); setActiveMenu(null) }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600
                                     hover:bg-red-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete User
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}

              {pagedUsers.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center">
                    <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-sm text-gray-400">No users match your search criteria</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-500">
            Showing {pagedUsers.length > 0 ? (page - 1) * PAGE_SIZE + 1 : 0} to{' '}
            {Math.min(page * PAGE_SIZE, filteredUsers.length)} of {filteredUsers.length} users
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded border border-gray-200 bg-white hover:bg-gray-50
                         disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-sm text-gray-600 px-2">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded border border-gray-200 bg-white hover:bg-gray-50
                         disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Close dropdown on outside click */}
      {activeMenu && (
        <div className="fixed inset-0 z-0" onClick={() => setActiveMenu(null)} />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete User</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this user? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium
                           text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium
                           hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite User Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-acad-500" />
                Invite User
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-1 rounded hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="user@university.edu.au"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-acad-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as UserRole)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white
                             focus:outline-none focus:ring-2 focus:ring-acad-500"
                >
                  {ROLES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institution</label>
                <input
                  type="text"
                  value={inviteInstitution}
                  onChange={(e) => setInviteInstitution(e.target.value)}
                  placeholder="University name"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm
                             focus:outline-none focus:ring-2 focus:ring-acad-500"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowInviteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium
                           text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleInvite}
                disabled={!inviteEmail}
                className="px-4 py-2 bg-acad-500 text-white rounded-lg text-sm font-medium
                           hover:bg-acad-600 transition-colors disabled:opacity-50"
              >
                Send Invitation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
